/* eslint-disable react-hooks/exhaustive-deps */
import React, { useEffect, useState } from "react";
import { useDisclosure } from "@jumbo/components/JumboDialog/hooks/useDisclosure";
import FormatListBulletedIcon from "@mui/icons-material/FormatListBulleted";
import Stack from "@mui/material/Stack";
import {
  Button,
  Grid,
  IconButton,
  InputAdornment,
  TextField,
  Typography,
} from "@mui/material";
import { Card, CardContent } from "@mui/material";
import LoadingButton from "@mui/lab/LoadingButton";
import ClearIcon from "@mui/icons-material/Clear";

import { usePagination } from "app/hooks/usePagination";
import { Table } from "app/components";
import Div from "@jumbo/shared/Div";
import { useDispatch, useSelector } from "react-redux";
import Loader from "@jumbo/components/Loader";
import { userType } from "app/helpers/userType";
import { auditList, AUDITS_ADD } from "app/routes/constant";
import { useNavigate } from "react-router-dom";
import moment from "moment";
import { getAuditList } from "app/redux/slice/auditSlice";
import { getProductList } from "app/redux/slice/productSlice";
import { getFabricList } from "app/redux/slice/fabricSlice";
import JumboAutoComplete from "@jumbo/components/JumboAutoComplete";

const Audit = () => {
  const { stageAction } = useDisclosure();
  const dispatch = useDispatch();
  const { isMA } = userType();
  const navigate = useNavigate();
  const productData = useSelector((state) => state?.product);
  const fabricData = useSelector((state) => state?.fabric);
  const auditData = useSelector((state) => state?.audit);
  
  // Load saved filters from localStorage on component mount
  const getSavedFilters = () => {
    try {
      const saved = localStorage.getItem('auditListFilters');
      return saved ? JSON.parse(saved) : {};
    } catch (error) {
      console.error('Error loading saved filters:', error);
      return {};
    }
  };

  const savedFilters = getSavedFilters();
  
  const [fromDate, setFromDate] = useState(savedFilters.fromDate || "");
  const [toDate, setToDate] = useState(savedFilters.toDate || "");
  const [searchText, setSearchText] = useState(savedFilters.searchText || "");
  const [order, setOrder] = useState(savedFilters.order || "desc");
  const [orderBy, setOrderBy] = useState(savedFilters.orderBy || "createdAt");
  const [productId, setProductId] = useState(savedFilters.productId || null);
  const [fabricId, setFabricId] = useState(savedFilters.fabricId || null);

  const { page, rowsPerPage, handleChangePage, handleChangeRowsPerPage } =
    usePagination({ id: "uniqueVariant" });

  const [permissionData] = useState(
    JSON.parse(localStorage.getItem("permission"))
  );

  const [permission, setPermission] = useState({
    createPermission: false,
    updatePermission: false,
    deletePermission: false,
    returnPermission: false,
  });

  // Save filters to localStorage
  const saveFilters = (filters) => {
    try {
      localStorage.setItem('auditListFilters', JSON.stringify(filters));
    } catch (error) {
      console.error('Error saving filters:', error);
    }
  };

  // Update saved filters when any filter changes
  const updateSavedFilters = (newFilters) => {
    const currentFilters = getSavedFilters();
    const updatedFilters = { ...currentFilters, ...newFilters };
    saveFilters(updatedFilters);
  };

  const getPermission = () => {
    if (isMA) {
      setPermission({
        createPermission: true,
        updatePermission: true,
        deletePermission: true,
        returnPermission: true,
      });
    } else {
      let permissionValue = {
        createPermission: false,
        updatePermission: false,
        deletePermission: false,
        returnPermission: false,
      };

      permissionData?.length &&
        permissionData.forEach(function (num) {
          if (num?.name === "create-audit") {
            permissionValue.createPermission = true;
          }
          if (num?.name === "delete-audit") {
            permissionValue.deletePermission = true;
          }
          if (num?.name === "update-audit") {
            permissionValue.updatePermission = true;
          }
          if (num?.name === "return-audit") {
            permissionValue.returnPermission = true;
          }
        });
      setPermission(permissionValue);
    }
  };

  const columns = [
    {
      id: "date",
      label: "Date",
      sort: true,
      render: (elm) => <> {moment(elm?.date).format("DD/MM/YYYY")}</>,
    },
    {
      id: "auditNo",
      label: "Audit No",
      sort: true,
    },
    {
      id: "productId",
      label: "Product",
      render: (row) => <>{row?.productId?.name || "-"}</>,
    },
    {
      id: "variantId",
      label: "Variant",
      render: (row) => <>{row?.variantId?.name || "-"}</>,
    },
    {
      id: "fabricId",
      label: "Fabric",
      render: (row) => <>{row?.fabricId?.name || "-"}</>,
    },
    {
      id: "totalQuantity",
      label: "Total QTY",
      sort: true,
    },
    {
      id: "badQuantity",
      label: "Bad",
      sort: true,
    },
    {
      id: "newFoundQuantity",
      label: "New Found",
      sort: true,
    },
    {
      id: "missingQuantity",
      label: "Missing",
      sort: true,
    },
    {
      id: "actions",
      label: "Actions",
      align: "center",
      minWidth: 160,
      render: (row) => (
        <>
          <IconButton
            disabled={row?.status === 1}
            onClick={() => {
              navigate(auditList(row._id));
            }}
          >
            <FormatListBulletedIcon />
          </IconButton>
        </>
      ),
    },
  ];

  const getData = () => {
    const value = {
      page: page,
      perPage: rowsPerPage,
      search: searchText.trim(),
      ...(fromDate && { fromDate }),
      ...(toDate && { toDate }),
      ...(productId && { productId: productId?._id }),
      ...(fabricId && { fabricId: fabricId?._id }),
    };
    if (orderBy !== "") {
      value.sort = order === "asc" ? 1 : -1;
      value.field = orderBy;
    }
    dispatch(getAuditList(value));
  };

  const handleKeyPress = (event) => {
    if (event.key === "Enter") {
      handleChangePage(0, 0);
      getData();
    }
  };

  const sortData = (Order, OrderBy) => {
    setOrder(Order);
    setOrderBy(OrderBy);
    updateSavedFilters({ order: Order, orderBy: OrderBy });
  };

  // Handle search text change
  const handleSearchChange = (value) => {
    setSearchText(value);
    updateSavedFilters({ searchText: value });
  };

  // Handle date changes
  const handleFromDateChange = (value) => {
    setFromDate(value);
    updateSavedFilters({ fromDate: value });
  };

  const handleToDateChange = (value) => {
    setToDate(value);
    updateSavedFilters({ toDate: value });
  };

  // Handle product selection
  const handleProductChange = (value) => {
    setProductId(value);
    updateSavedFilters({ productId: value });
  };

  // Handle fabric selection
  const handleFabricChange = (value) => {
    setFabricId(value);
    updateSavedFilters({ fabricId: value });
  };

  // Clear all filters
  const clearAllFilters = () => {
    setFromDate("");
    setToDate("");
    setSearchText("");
    setProductId(null);
    setFabricId(null);
    localStorage.removeItem('auditListFilters');
    handleChangePage(0, 0);
    getData();
  };

  useEffect(() => {
    getPermission();
    dispatch(getProductList({ sort: 1, field: "name" }));
    dispatch(getFabricList());
  }, []);

  useEffect(() => {
    getData();
  }, [page, rowsPerPage, dispatch, order, orderBy]);

  return (
    <>
      {auditData?.loading ? (
        <Loader />
      ) : (
        <>
          <Card sx={{ display: "flex", mb: 3 }}>
            <Div sx={{ display: "flex", flexDirection: "column", flex: "1" }}>
              <CardContent>
                <Grid container spacing={{ xs: 1, md: 3 }}>
                  <Grid item xs={12} sm={6}>
                    <TextField
                      fullWidth
                      size="small"
                      label="Search"
                      value={searchText}
                      onChange={(e) => handleSearchChange(e.target.value)}
                      onKeyPress={handleKeyPress}
                      InputProps={{
                        endAdornment: (
                          <InputAdornment
                            position="end"
                            style={{
                              display: searchText === "" ? "none" : "flex",
                            }}
                            onClick={() => handleSearchChange("")}
                          >
                            <ClearIcon />
                          </InputAdornment>
                        ),
                      }}
                    />
                  </Grid>
                  <Grid item xs={12} sm={3}>
                    <TextField
                      type="date"
                      label={"From Date"}
                      name="fromDate"
                      InputLabelProps={{ shrink: true }}
                      fullWidth
                      required
                      size="small"
                      value={fromDate}
                      onChange={(evt) => handleFromDateChange(evt.target.value)}
                    />
                  </Grid>
                  <Grid item xs={12} sm={3}>
                    <TextField
                      type="date"
                      label={"To Date"}
                      name="toDate"
                      InputLabelProps={{ shrink: true }}
                      fullWidth
                      required
                      size="small"
                      value={toDate}
                      onChange={(evt) => handleToDateChange(evt.target.value)}
                    />
                  </Grid>
                  <Grid item xs={12} sm={4}>
                    <JumboAutoComplete
                      options={productData?.list || []}
                      label="Product"
                      size="small"
                      getOptionLabel={(option) => option?.name}
                      name={`productId`}
                      value={productId}
                      onChange={(evt, value) => handleProductChange(value)}
                      isOptionEqualToValue={(option, value) =>
                        option?._id === value?._id
                      }
                    />
                  </Grid>
                  <Grid item xs={12} sm={4}>
                    <JumboAutoComplete
                      options={fabricData?.list || []}
                      label="Fabric"
                      size="small"
                      getOptionLabel={(option) => option?.name}
                      name={`fabricId`}
                      value={fabricId}
                      onChange={(evt, value) => handleFabricChange(value)}
                      isOptionEqualToValue={(option, value) =>
                        option?._id === value?._id
                      }
                    />
                  </Grid>
                  <Grid item xs={12} sm={2}>
                    <Button
                      fullWidth
                      type="submit"
                      variant="contained"
                      onClick={() => {
                        handleChangePage(0, 0);
                        getData();
                      }}
                    >
                      Submit
                    </Button>
                  </Grid>
                  {/* <Grid item xs={12} sm={2}>
                    <Button
                      fullWidth
                      type="button"
                      variant="outlined"
                      onClick={clearAllFilters}
                    >
                      Clear Filters
                    </Button>
                  </Grid> */}
                </Grid>
              </CardContent>
            </Div>
          </Card>
          <Card>
            <CardContent>
              <Stack
                justifyContent="space-between"
                direction={{ xs: "column", sm: "row" }}
                sx={{ mb: 3 }}
              >
                <Typography variant={"h2"} mb={0}>
                  Audit List
                </Typography>
                {permission.createPermission && (
                  <LoadingButton
                    onClick={() => {
                      navigate(AUDITS_ADD);
                    }}
                    type="submit"
                    variant="contained"
                    sx={{
                      mt: { xs: 1, md: 0 },
                    }}
                  >
                    Add Audit
                  </LoadingButton>
                )}
              </Stack>
              <Table
                columns={columns}
                rows={auditData?.list}
                page={page}
                rowsPerPage={rowsPerPage}
                Order={order}
                count={auditData?.total}
                OrderBy={orderBy}
                sortData={sortData}
                handleChangePage={handleChangePage}
                handleChangeRowsPerPage={handleChangeRowsPerPage}
                editAction={stageAction}
              />
            </CardContent>
          </Card>
        </>
      )}
    </>
  );
};

export default Audit;
